import { watch } from 'vue';
import { acceptHMRUpdate, defineStore } from 'pinia';
import get from 'lodash/get';
import isEmpty from 'lodash/isEmpty';
import cloneDeep from 'lodash/cloneDeep';
import { getSampleDataSubName } from '@/free/free.utils.js';
import { setGlobalSessionData, clearGlobalSessionData, updateActiveUserProfile, getActiveUser } from '@/data/session/session-globals';
import { USER_ROLES } from '@/constants';

import { prepareAccessControlFromUser } from '@/stores/helpers/auth-access-control.helper';
import {
    syncPermissionsFromAccessControl,
    getUserFromUserData,
    turnOffSampleData,
    isSampleDataTurnedOn,
    canViewSessionReplayForApp,
} from '@/stores/helpers/auth.helper';
import {
    convertAdminRoleToFlag,
    canManageServiceAccounts,
    hasGlobalAccess,
    hasAdminAccess,
    isAdmin,
    isV2SubAdmin,
    isAdoptManager,
    isGuest,
    isOps,
    isPendozer,
    isReadOnly,
    isSuper,
    isSupport,
    isUser,
    roleName,
} from '@/stores/helpers/auth-roles.helper';
import {
    fetchUser,
    fetchUserDetails,
    login,
    logout,
    update,
    updatePassword,
    updateUsername,
    updateEmail,
    fetchUnauthenticatedTrainingAttributes,
} from '@/stores/api/auth.api';
import { getSubIdFromUrlPath } from '@utils/sub-id-routing.utils';
import { http } from '@pendo/http';
import { $canOrg, $can } from '@pendo/permissions';
import { mobilePairingDisconnect } from '@pendo/services/MobilePairing';
import {
    localSegmentFlagsExist,
    setSegmentFlags as setLocalSegmentFlags,
    hasSegmentFlag as hasLocalStorageSegmentFlag,
} from '@/data/flags/local-flags';
import { useSegmentFlagOverride } from '@/utils/segment-flag.utils';
import type { Subscription } from '@/stores/subscription.store';
import type { ProductLine } from '@/stores/product-lines.store';
import { LOAD_STATUS, type LoadStatus } from '@/stores/constants/load-status';

export interface AuthUser {
    id?: string;
    subscriptionId?: string;
    username?: string;
    first?: string;
    last?: string;
    jobRole?: string;
    useCase?: string;
    locale?: string;
    role?: number;
    expiresAt?: number;
    globalAccessExpiresAt?: number;
    isPendozer?: boolean;
    flags?: Record<string, boolean>;
    userflags?: Record<string, boolean>;
    applicationAccessControl?: Record<string, unknown[]>;
    subscriptionAccessControl?: unknown[];
    organizationAccessControl?: unknown[];
    activeOrganization?: Record<string, unknown>;
    vendorUserId?: string;
    vendorUserPermissionId?: string | number;
    [key: string]: unknown;
}

interface AuthState {
    authenticated: boolean;
    initialized: boolean;
    hasLoginError: boolean;
    hasUpdateSettingsError: boolean;
    redirectUrl: string | null | undefined;
    segmentFlags: string[];
    segmentFlagsLoadStatus: LoadStatus;
    segmentFlagsReceived: boolean;
    user: AuthUser;
    sessionLoadStatus: LoadStatus;
    userLoadStatus: LoadStatus;
    preSampleDataSubId: string | null;
    subAccessError: { id: string } | null;
    onlyTrainingSub: boolean;
    onlyNovusSub: boolean;
    awaitingEmailVerification: boolean;
    unauthenticatedTrainingAttributes: Record<string, unknown>;
}

interface AuthInitSubscription extends Subscription {
    id: string;
    name?: string;
    value?: string;
    applications?: unknown[];
    productLines?: ProductLine[];
}

interface AuthInitUser extends AuthUser {
    userid: string;
    flags: Record<string, boolean>;
}

interface AuthInitData {
    subscriptions: AuthInitSubscription[];
    activeSubscription: AuthInitSubscription;
    activeOrganization: Record<string, unknown>;
    user: AuthInitUser;
    intercomChatJwt: string;
    [key: string]: unknown;
}

interface GuideStep {
    templateId?: string;
}

interface Guide {
    state?: string;
    steps?: GuideStep[];
}

type AuthSubscriptionStore = ReturnType<typeof import('@/stores/subscription.store').useSubscriptionStore>;
type AuthAppsStore = ReturnType<typeof import('@/stores/apps.store').useAppsStore>;
type AuthProductLinesStore = ReturnType<typeof import('@/stores/product-lines.store').useProductLinesStore>;

// The package exports this at runtime but omits it from its type declarations.
// eslint-disable-next-line @typescript-eslint/no-require-imports
const { initPermissions } = require('@pendo/permissions') as {
    initPermissions: (accessControl: Record<string, unknown>) => void;
};

function getSubscriptionStore (): AuthSubscriptionStore {
    // Lazy to avoid the auth -> subscription -> auth initialization cycle.
    // eslint-disable-next-line @typescript-eslint/no-require-imports
    const { useSubscriptionStore }: typeof import('@/stores/subscription.store') = require('@/stores/subscription.store');

    return useSubscriptionStore();
}

function getAppsStore (): AuthAppsStore {
    // Lazy to avoid the auth -> apps -> user-settings -> auth initialization cycle.
    // eslint-disable-next-line @typescript-eslint/no-require-imports
    const { useAppsStore }: typeof import('@/stores/apps.store') = require('@/stores/apps.store');

    return useAppsStore();
}

function getProductLinesStore (): AuthProductLinesStore {
    // eslint-disable-next-line @typescript-eslint/no-require-imports
    const { useProductLinesStore }: typeof import('@/stores/product-lines.store') = require('@/stores/product-lines.store');

    return useProductLinesStore();
}

function waitForLoadStatusComplete (getLoadStatus: () => LoadStatus) {
    return new Promise<void>((resolve) => {
        if (getLoadStatus() === LOAD_STATUS.COMPLETE) return resolve();

        const unwatch = watch(getLoadStatus, (status) => {
            if (status === LOAD_STATUS.COMPLETE) {
                unwatch();
                resolve();
            }
        });
    });
}

export function getInitialState (): AuthState {
    return {
        authenticated: false,
        initialized: false,
        hasLoginError: false,
        hasUpdateSettingsError: false,
        redirectUrl: null,
        segmentFlags: [],
        segmentFlagsLoadStatus: LOAD_STATUS.NOT_STARTED,
        segmentFlagsReceived: false,
        user: {},
        sessionLoadStatus: LOAD_STATUS.NOT_STARTED,
        userLoadStatus: LOAD_STATUS.NOT_STARTED,
        preSampleDataSubId: null,
        subAccessError: null,
        onlyTrainingSub: false,
        onlyNovusSub: false,
        awaitingEmailVerification: false,
        unauthenticatedTrainingAttributes: {},
    };
}

export const useAuthStore = defineStore('auth', {
    state: getInitialState,

    actions: {
        setUserLoadStatus ({ status }: { status: LoadStatus }) {
            this.userLoadStatus = status;
        },
        setSessionLoadStatus ({ status }: { status: LoadStatus }) {
            this.sessionLoadStatus = status;
        },
        setAuthenticated ({ authenticated }: { authenticated: boolean }) {
            this.authenticated = authenticated;
        },
        setInitialized ({ initialized }: { initialized: boolean }) {
            this.initialized = initialized;
        },
        setUser ({ user }: { user: AuthUser }) {
            this.user = user;
        },
        setProperty ({ property, value }: { property: string; value: unknown; reIdentify?: boolean }) {
            this.user[property] = value;
        },
        setUserSegmentFlags ({ flags }: { flags: string[] }) {
            setLocalSegmentFlags(flags);
            this.segmentFlags = flags;
        },
        setSegmentFlagsLoadStatus ({ status }: { status: LoadStatus }) {
            this.segmentFlagsLoadStatus = status;
        },
        setSegmentFlagsReceived () {
            this.segmentFlagsReceived = true;
        },
        setLoginError ({ hasLoginError }: { hasLoginError: boolean }) {
            this.hasLoginError = hasLoginError;
        },
        setUpdateSettingsError ({ hasUpdateSettingsError }: { hasUpdateSettingsError: boolean }) {
            this.hasUpdateSettingsError = hasUpdateSettingsError;
        },
        setRedirectUrl ({ redirectUrl }: { redirectUrl: string | null | undefined }) {
            this.redirectUrl = redirectUrl;
        },
        setPreSampleDataSubId ({ id }: { id: string | null }) {
            this.preSampleDataSubId = id;
        },
        setSubAccessError ({ error }: { error: { id: string } | null }) {
            this.subAccessError = error;
        },
        reset () {
            const { unauthenticatedTrainingAttributes } = this;

            Object.assign(this.$state, getInitialState(), {
                unauthenticatedTrainingAttributes: unauthenticatedTrainingAttributes || {},
            });
        },
        setOnlyTrainingSub ({ onlyTrainingSub }: { onlyTrainingSub: boolean }) {
            this.onlyTrainingSub = onlyTrainingSub;
        },
        setOnlyNovusSub ({ onlyNovusSub }: { onlyNovusSub: boolean }) {
            this.onlyNovusSub = onlyNovusSub;
        },
        setAwaitingEmailVerification ({ awaitingEmailVerification }: { awaitingEmailVerification: boolean }) {
            this.awaitingEmailVerification = awaitingEmailVerification;
        },
        setUnauthenticatedTrainingAttributes (
            { unauthenticatedTrainingAttributes }: { unauthenticatedTrainingAttributes: Record<string, unknown> },
        ) {
            this.unauthenticatedTrainingAttributes = unauthenticatedTrainingAttributes;
        },
        init () {
            this.setupSegmentFlagListeners();
            this.getUser();
        },
        setupSegmentFlagListeners () {
            const updateFlags = (event?: Event) => {
                const segmentFlags = window.pendo?.segmentFlags || [];
                this.setUserSegmentFlags({ flags: segmentFlags });
                // Only treat real agent events as "received" — the sync-init read
                // may see a placeholder empty array before the agent has actually
                // delivered flags, which would defeat waitForSegmentFlagsReceived.
                if (event) {
                    this.setSegmentFlagsReceived();
                }
                this.setSegmentFlagsLoadStatus({ status: LOAD_STATUS.COMPLETE });
            };

            document.addEventListener('pendo_segmentFlagsReady', updateFlags);
            document.addEventListener('pendo_segmentFlagsUpdated', updateFlags);
            document.addEventListener('pendo_segmentFlagsError', updateFlags);

            if (window.pendo?.segmentFlags) {
                updateFlags();
            }
        },
        async waitForSegmentFlags () {
            return new Promise<void>((resolve) => {
                if (this.segmentFlagsLoadStatus === LOAD_STATUS.COMPLETE) return resolve();

                const unwatch = watch(
                    () => this.segmentFlagsLoadStatus,
                    (status) => {
                        if (status === LOAD_STATUS.COMPLETE) {
                            unwatch();
                            resolve();
                        }
                    },
                );

                setTimeout(() => { // Failsafe in case segment flags never seem to load
                    if (this.segmentFlagsLoadStatus === LOAD_STATUS.COMPLETE) return;

                    console.warn('Pendo SDK is ready but segment flags have not loaded. This may be due to an issue with the Pendo snippet or network connectivity.'); // eslint-disable-line no-console
                    unwatch();
                    this.setSegmentFlagsLoadStatus({ status: LOAD_STATUS.COMPLETE });
                    resolve();
                }, 250);
            });
        },
        // Unlike waitForSegmentFlags, this waits for the agent to actually deliver
        // flags (or error out) rather than treating the short failsafe as loaded.
        async waitForSegmentFlagsReceived ({ timeout = 1500 }: { timeout?: number } = {}) {
            return new Promise<void>((resolve) => {
                if (this.segmentFlagsReceived) return resolve();

                const unwatch = watch(
                    () => this.segmentFlagsReceived,
                    (received) => {
                        if (received) {
                            unwatch();
                            resolve();
                        }
                    },
                );

                setTimeout(() => { // Failsafe in case the agent never delivers flags
                    if (this.segmentFlagsReceived) return;

                    unwatch();
                    resolve();
                }, timeout);
            });
        },
        async waitForUserLoad () {
            await waitForLoadStatusComplete(() => this.userLoadStatus);
        },
        async waitForSessionLoad () {
            await waitForLoadStatusComplete(() => this.sessionLoadStatus);
        },
        async getUser () {
            this.setSessionLoadStatus({ status: LOAD_STATUS.PENDING });
            this.setUserLoadStatus({ status: LOAD_STATUS.PENDING });
            let userData: AuthInitData;

            try {
                try {
                    userData = await fetchUser() as AuthInitData;
                } catch (error) {
                    const [{ identifyAnonymous }, { refreshLocale }] = await Promise.all([
                        import('@/plugins/analytics'),
                        import('@/utils/i18n/i18n-locale-resolver'),
                    ]);
                    identifyAnonymous();
                    clearGlobalSessionData(<Error>(error));
                    refreshLocale();
                    throw error;
                }

                const subscriptionList = get(userData, 'subscriptions', []);
                const sampleDataSubName = getSampleDataSubName();
                const sampleDataSub = subscriptionList.find((sub) => get(sub, 'name', undefined) === sampleDataSubName);
                const sampleDataSubId = get(sampleDataSub, 'id', undefined);
                const sampleDataSubIsActive = sampleDataSubId === get(userData, 'activeSubscription.id');
                const preSampleDataSub = subscriptionList.find(
                    (sub) => get(sub, 'featureFlags.pendoFree', null) && sub.id !== sampleDataSubId,
                );

                if (preSampleDataSub) {
                    this.setPreSampleDataSubId({ id: preSampleDataSub.id });
                }

                if (isSampleDataTurnedOn() || sampleDataSubIsActive) {
                    if (sampleDataSub && window.location.pathname !== '/post-register-steps') {
                        userData.user.flags.admin = false;
                        userData.user.flags.readOnly = true;
                        userData.user.applicationAccessControl = {};
                        userData.user.subscriptionAccessControl = [];
                        userData.user.organizationAccessControl = [];
                        userData.activeSubscription.value = sampleDataSubId;
                    } else {
                        turnOffSampleData();

                        // if the pendoFree sub has been upgraded while the user is in demo-mode, default to the first paid sub
                        if (userData.activeSubscription) {
                            const defaultSub = subscriptionList.find((sub) => sub.id !== sampleDataSubId);
                            userData.activeSubscription.value = get(defaultSub, 'id', undefined);
                        }
                    }
                }

                const currentRouteSubId = getSubIdFromUrlPath();
                if (currentRouteSubId && currentRouteSubId !== userData.activeSubscription.id) {
                    try {
                        await http.put(`/api/user/${userData.user.userid}/setsubscription/${currentRouteSubId}`);
                        userData = await fetchUser() as AuthInitData;
                    } catch (_err) {
                        console.error(`Cannot set active subscription to ${currentRouteSubId}`, _err);
                        this.setSubAccessError({ error: { id: currentRouteSubId } });
                    }
                }

                const { activeOrganization } = userData;
                const user = getUserFromUserData(userData);

                setGlobalSessionData({ ...userData, user });
                this.setUser({ user });
                const { useOrganizationStore } = await import('@/stores/organization.store');
                useOrganizationStore().setActive(activeOrganization);
                this.setAuthenticated({ authenticated: true });
                await this.hydrate(userData);

                initPermissions(prepareAccessControlFromUser(user));
            } catch (error) {
                this.setAuthenticated({ authenticated: false });
                this.setUserLoadStatus({
                    status: LOAD_STATUS.COMPLETE,
                });

                throw error;
            } finally {
                this.setSessionLoadStatus({ status: LOAD_STATUS.COMPLETE });
            }

            const [{ useUserSettingsStore }, { useGlobalFiltersStore }] = await Promise.all([
                import('@/stores/user-settings.store'),
                import('@/stores/global-filters.store'),
            ]);
            const userSettingsStore = useUserSettingsStore();
            const userDetailRequests = [
                userSettingsStore.loadAll({ reFetch: true }),
                this.getUserDetails(),
            ];

            await Promise.all(userDetailRequests);

            useGlobalFiltersStore().loadInitialStateFromUserSettings();

            const [{ identify }, { registerIntercom }, { connectToWebSocketServer }] = await Promise.all([
                import('@/plugins/analytics'),
                import('@/utils/intercom.js'),
                import('@/comments/comments.utils'),
            ]);
            identify();
            registerIntercom(userData.intercomChatJwt);

            this.setUserLoadStatus({ status: LOAD_STATUS.COMPLETE });

            connectToWebSocketServer();

            if (window.Cypress) {
                window.authInitReady = true;
            }
        },
        async refreshApplicationPermissions () {
            let userData: AuthInitData;

            try {
                userData = await fetchUser() as AuthInitData;
            } catch (error) {
                console.error('[auth] refreshApplicationPermissions: failed to fetch user', error);

                throw error;
            }

            await this.hydrate(userData);

            const user = getUserFromUserData(userData);
            const accessControl = prepareAccessControlFromUser(user);

            try {
                syncPermissionsFromAccessControl(accessControl);
            } catch (error) {
                console.error('[auth] refreshApplicationPermissions: failed to sync permissions', error);

                throw error;
            }

            this.setProperty({
                property: 'applicationAccessControl',
                value: accessControl.applicationAccessControl,
            });
            this.setProperty({
                property: 'subscriptionAccessControl',
                value: accessControl.subscriptionAccessControl,
            });
            this.setProperty({
                property: 'organizationAccessControl',
                value: accessControl.organizationAccessControl,
            });
        },
        // /api/init only carries session-derived fields, so these come from the user record
        async getUserDetails () {
            if (this.onlyTrainingSub || this.onlyNovusSub) {
                return;
            }

            const {
                first,
                last,
                vendorUserId,
                vendorUserPermissionId,
                jobRole,
                useCase,
                locale,
            } = await fetchUserDetails(this.user.id);

            // reIdentify prop is passed here to avoid reidentifying needlessly
            // within the analytics plugin
            this.setProperty({ property: 'first', value: first, reIdentify: false });
            this.setProperty({ property: 'last', value: last, reIdentify: false });
            this.setProperty({ property: 'jobRole', value: jobRole, reIdentify: false });
            this.setProperty({ property: 'useCase', value: useCase, reIdentify: false });
            this.setProperty({ property: 'locale', value: locale, reIdentify: false });

            if (vendorUserId) {
                this.setProperty({
                    property: 'vendorUserId',
                    value: vendorUserId,
                });
            }

            if (vendorUserPermissionId) {
                this.setProperty({
                    property: 'vendorUserPermissionId',
                    value: vendorUserPermissionId,
                });
            }

            updateActiveUserProfile({
                firstName: first,
                lastName: last,
                fullName: [first, last].filter(Boolean).join(' ') || undefined,
            });
        },
        async hydrate (
            { subscriptions, activeSubscription }: {
                subscriptions: AuthInitSubscription[];
                activeSubscription: AuthInitSubscription;
            },
        ) {
            await getSubscriptionStore().hydrate({ subscriptions, activeSubscription });

            if (!activeSubscription) {
                return;
            }

            const { productLines, applications } = activeSubscription;
            await getAppsStore().hydrate({ applications });
            await getProductLinesStore().hydrate({ productLines });
        },
        updateOnlyTrainingSub ({ onlyTrainingSub }: { onlyTrainingSub: boolean }) {
            this.setOnlyTrainingSub({ onlyTrainingSub });
        },
        updateOnlyNovusSub ({ onlyNovusSub }: { onlyNovusSub: boolean }) {
            this.setOnlyNovusSub({ onlyNovusSub });
        },
        async login ({
            username,
            password,
            queryString,
            postLogin = false,
            data = {},
        }: {
            username?: string;
            password?: string;
            queryString?: string;
            postLogin?: boolean;
            data?: { redirectUrl?: string };
        } = {}) {
            try {
                if (!postLogin) {
                    data = await login(username, password, queryString);
                }
                const { redirectUrl } = data;
                this.setRedirectUrl({ redirectUrl });
            } catch {
                this.setLoginError({ hasLoginError: true });
            }
        },
        async logout () {
            const [
                { usePendoCollaborationStore },
                { useUserSettingsStore },
                { useHistoryStore },
                { useGroupsStore },
                { useTeamsStore },
            ] = await Promise.all([
                import('@/stores/pendo-collaboration.store'),
                import('@/stores/user-settings.store'),
                import('@/stores/history.store'),
                import('@/stores/groups.store'),
                import('@/stores/teams.store'),
            ]);
            usePendoCollaborationStore().teardown();
            useUserSettingsStore().teardown();
            useHistoryStore().teardown();
            useGroupsStore().teardown();
            useTeamsStore().teardown();
            mobilePairingDisconnect();
            await logout();
        },
        async changePassword ({ oldPass, newPass }: { oldPass: string; newPass: string }) {
            this.setUpdateSettingsError({ hasUpdateSettingsError: false });
            try {
                await updatePassword(oldPass, newPass);
            } catch (error) {
                this.setUpdateSettingsError({
                    hasUpdateSettingsError: true,
                });
                throw error;
            }
        },
        async changeEmail ({ newUsername }: { newUsername: string }) {
            this.setUpdateSettingsError({ hasUpdateSettingsError: false });
            try {
                await updateEmail(newUsername, this.userName);
                this.setAwaitingEmailVerification({
                    awaitingEmailVerification: true,
                });
            } catch (error) {
                this.setUpdateSettingsError({
                    hasUpdateSettingsError: true,
                });
                throw error;
            }
        },
        async changeUsername ({ newUsername, password }: { newUsername: string; password: string }) {
            this.setUpdateSettingsError({ hasUpdateSettingsError: false });
            try {
                await updateUsername(newUsername, password);
                this.setProperty({
                    property: 'username',
                    value: newUsername,
                });
            } catch (error) {
                this.setUpdateSettingsError({
                    hasUpdateSettingsError: true,
                });
                throw error;
            }
        },
        async updateFirstName ({ firstName }: { firstName: string }) {
            this.setUpdateSettingsError({ hasUpdateSettingsError: false });
            try {
                const user = convertAdminRoleToFlag(cloneDeep(this.user));
                await update({ ...user, first: firstName });
                this.setProperty({ property: 'first', value: firstName });
                const lastName = this.user.last ?? getActiveUser()?.lastName;
                updateActiveUserProfile({
                    firstName,
                    fullName: [firstName, lastName].filter(Boolean).join(' ') || undefined,
                });
            } catch (error) {
                this.setUpdateSettingsError({
                    hasUpdateSettingsError: true,
                });
                throw error;
            }
        },
        async updateLastName ({ lastName }: { lastName: string }) {
            this.setUpdateSettingsError({ hasUpdateSettingsError: false });
            try {
                const user = convertAdminRoleToFlag(cloneDeep(this.user));
                await update({ ...user, last: lastName });
                this.setProperty({ property: 'last', value: lastName });
                const firstName = this.user.first ?? getActiveUser()?.firstName;
                updateActiveUserProfile({
                    lastName,
                    fullName: [firstName, lastName].filter(Boolean).join(' ') || undefined,
                });
            } catch (error) {
                this.setUpdateSettingsError({
                    hasUpdateSettingsError: true,
                });
                throw error;
            }
        },
        async updateUserProperties (properties: Record<string, unknown>) {
            this.setUpdateSettingsError({ hasUpdateSettingsError: false });
            try {
                const user = convertAdminRoleToFlag(cloneDeep(this.user));
                await update({ ...user, ...properties });

                Object.keys(properties).forEach((key) => {
                    this.setProperty({ property: key, value: properties[key], reIdentify: true });
                });
            } catch (error) {
                this.setUpdateSettingsError({
                    hasUpdateSettingsError: true,
                });
                throw error;
            }
        },
        async updateUseCase ({ useCase }: { useCase: string }) {
            this.setUpdateSettingsError({ hasUpdateSettingsError: false });
            try {
                const user = convertAdminRoleToFlag(cloneDeep(this.user));
                await update({ ...user, useCase });
                this.setProperty({ property: 'useCase', value: useCase, reIdentify: true });
            } catch (error) {
                this.setUpdateSettingsError({
                    hasUpdateSettingsError: true,
                });
                throw error;
            }
        },
        async updateJobRoleAndUseCase ({ useCase, jobRole }: { useCase: string; jobRole: string }) {
            this.setUpdateSettingsError({ hasUpdateSettingsError: false });
            try {
                const user = convertAdminRoleToFlag(cloneDeep(this.user));
                await update({ ...user, useCase, jobRole });
                this.setProperty({ property: 'useCase', value: useCase, reIdentify: true });
                this.setProperty({ property: 'jobRole', value: jobRole, reIdentify: true });
            } catch (error) {
                this.setUpdateSettingsError({
                    hasUpdateSettingsError: true,
                });
                throw error;
            }
        },
        async updateUserLocale ({ locale }: { locale: string }) {
            this.setUpdateSettingsError({ hasUpdateSettingsError: false });
            try {
                const user = convertAdminRoleToFlag(cloneDeep(this.user));
                await update({ ...user, locale });
                this.setProperty({ property: 'locale', value: locale });
                const { rememberLocale } = await import('@/utils/i18n/i18n-locale-resolver');
                rememberLocale(locale);
            } catch (error) {
                this.setUpdateSettingsError({
                    hasUpdateSettingsError: true,
                });
                throw error;
            }
        },
        async fetchUnauthenticatedTrainingAttributes () {
            const unauthenticatedTrainingAttributes = await fetchUnauthenticatedTrainingAttributes();

            this.setUnauthenticatedTrainingAttributes(
                { unauthenticatedTrainingAttributes: unauthenticatedTrainingAttributes || {} },
            );
        },
    },

    // Auth getters are for the signed-in user only; per-userId checks belong in users store.
    getters: {
        userId (state): string | undefined {
            return state.user?.id;
        },
        userName (state): string | undefined {
            return state.user?.username;
        },
        firstName (state): string | undefined {
            return state.user?.first;
        },
        lastName (state): string | undefined {
            return state.user?.last;
        },
        userLocale (state): string | undefined {
            return state.user?.locale;
        },
        isLoadingSegmentFlags (state): boolean {
            return !localSegmentFlagsExist() && state.segmentFlagsLoadStatus === LOAD_STATUS.PENDING;
        },
        isLoadedSegmentFlags (state): () => boolean {
            return function () {
                return localSegmentFlagsExist() || state.segmentFlagsLoadStatus === LOAD_STATUS.COMPLETE;
            };
        },
        isAuthenticated (state): boolean {
            return !isEmpty(state.user) && state.authenticated;
        },
        isLoading (state): boolean {
            return state.userLoadStatus === LOAD_STATUS.PENDING;
        },
        // Returns a function so callers can watch isLoaded while /api/init is in flight.
        isLoaded (state): () => boolean {
            return function () {
                return state.userLoadStatus === LOAD_STATUS.COMPLETE;
            };
        },
        hasCompletedUserLoad (state): boolean {
            return state.userLoadStatus === LOAD_STATUS.COMPLETE;
        },
        hasOnlyTrainingSub (state): boolean {
            return state.onlyTrainingSub;
        },
        hasOnlyNovusSub (state): boolean {
            return state.onlyNovusSub;
        },
        isPublic (): boolean {
            return !this.isAuthenticated;
        },
        isReadOnly: (state) => isReadOnly(state.user),
        isUser: (state) => isUser(state.user),
        isPendozer: (state) => isPendozer(state.user),
        isAdmin: (state) => isAdmin(state.user),
        isAdoptManager (state): boolean {
            return isAdoptManager(state.user);
        },
        isSuper: (state) => isSuper(state.user),
        isSupport: (state) => isSupport(state.user),
        isOps: (state) => isOps(state.user),
        isGuest: (state) => isGuest(state.user),
        isImpersonating (): boolean {
            if (this.isGuest) {
                return true;
            }

            if (this.hasSuperAccess) {
                const { active, map } = getSubscriptionStore();

                return !active.id || !map[active.id];
            }

            return false;
        },
        isAuthorizedForRoute (): (accessLevel?: number | 'OPS' | 'ORG_ADMIN') => boolean {
            return (accessLevel) => {
                if (!accessLevel || accessLevel === 1) {
                    return true;
                }

                if (accessLevel === 'OPS') {
                    return this.isOps;
                }

                if (accessLevel === 'ORG_ADMIN') {
                    return ($canOrg()
                        .entity('organization')
                        .action('view') || this.isOps);
                }

                return (this.user.role ?? 0) >= accessLevel;
            };
        },
        hasUserAccess: (state) => (state.user.role ?? 0) >= USER_ROLES.USER,
        hasAdminAccess: (state) => hasAdminAccess(state.user),
        hasV2SubAdminAccess: (state) => isV2SubAdmin(state.user),
        canManageServiceAccounts: (state) =>
            canManageServiceAccounts(state.user, getSubscriptionStore().getFeatureFlag('enableRolesAndPermissionV2')),
        hasGlobalAccess: (state) => hasGlobalAccess(state.user),
        hasSuperAccess: (state) => (state.user.role ?? 0) >= USER_ROLES.SUPER,
        hasOpsAccess: (state) => isOps(state.user),
        hasPermission (): (permission: string) => boolean {
            return (permission) => {
                // super/ops may or may not have flags set, but have permission regardless
                if (this.isSuper || this.isOps) {
                    return true;
                }
                // with beta publishing control, user AND admin role flags are editable
                if (getSubscriptionStore().getFeatureFlag('guidePublishControl') || this.isUser) {
                    return get(this.user, `flags.${permission}`, false) as boolean;
                }
                // admins without beta publishing control have all permissions
                if (this.hasAdminAccess) {
                    return true;
                }

                return false;
            };
        },
        roleName: (state) => roleName(state.user),
        flagList (): string[] | undefined {
            if (this.isSuper) {
                return [];
            }

            const { flags } = this.user;
            if (!flags) {
                return;
            }

            return Object.keys(flags).filter((flag) => flags[flag]);
        },
        canEditGuides (): boolean {
            const canEditTemplate = this.hasPermission('allowGuideEditViaTemplate');
            const canEditSource = this.hasPermission('allowGuideEditViaSource');

            return !!canEditTemplate || !!canEditSource;
        },
        canEditGuide (): (guide: Guide) => boolean {
            return (guide) => {
                if (this.isReadOnly) {
                    return false;
                }

                const isPublishedGuide = guide.state === 'public';
                const guideHasSteps = Array.isArray(guide.steps) && !!guide.steps.length;

                const canEditAnyStep = (steps: GuideStep[]) => steps.some((step) => this.canEditStep(step));

                if (!isPublishedGuide) {
                    if (guideHasSteps) {
                        return canEditAnyStep(guide.steps || []);
                    }

                    return true;
                }

                const canManagePublishedGuide = this.hasPermission('allowGuidePublish');
                if (canManagePublishedGuide) {
                    if (guideHasSteps) {
                        return canEditAnyStep(guide.steps || []);
                    }

                    return true;
                }

                return false;
            };
        },
        canEditStep (): (step: GuideStep) => boolean {
            return (step) => {
                if (this.isReadOnly) {
                    return false;
                }

                if (step.templateId) {
                    return this.hasPermission('allowGuideEditViaTemplate');
                }

                return this.hasPermission('allowGuideEditViaSource');
            };
        },
        // This method only determines if the user has multiple subs and has the correct flags
        // for copy guide/rc to be enabled. It is still important to check if the user has the
        // correct create permission
        canCopyGuidesToSubscription: () => () => {
            const subscriptionStore = getSubscriptionStore();
            const subs = subscriptionStore.allSubscriptions;
            if (subs.length <= 1) return false;

            const hasEnableGuideCopyFlag = subscriptionStore.getFeatureFlag('enableGuideCopy');
            if (subscriptionStore.getFeatureFlag('enableGuideCopyEditRestrictions')) return false;

            if (hasEnableGuideCopyFlag) return true;
            if (getProductLinesStore().hasProductLinePurchased('adoptReseller')) return true;

            return false;
        },
        canCopyRCToSubscription (): () => boolean {
            return () => {
                const subscriptionStore = getSubscriptionStore();
                const subs = subscriptionStore.list;
                if (subs.length <= 1) return false;

                if (!this.hasSegmentFlag('copyGuides')) return false;
                if (subscriptionStore.getFeatureFlag('enableGuideCopyEditRestrictions')) return false;

                if (subscriptionStore.getFeatureFlag('enableGuideCopy')) {
                    return true;
                }

                return false;
            };
        },
        canCreatePages (): boolean {
            const appMap = getAppsStore().map;
            const canAppCreatePages = (appId: string) =>
                $can(appId)
                    .entity('page')
                    .action('create');

            return Object.keys(appMap).some(canAppCreatePages);
        },
        appIdsWithSessionReplayRoleEnabled (): number[] {
            let appIdStrings;

            const appMap = getAppsStore().map;
            if (this.hasAdminAccess) {
                appIdStrings = Object.keys(appMap);
            } else {
                appIdStrings = Object.keys(appMap).filter(canViewSessionReplayForApp);
            }

            return appIdStrings.map(Number);
        },
        hasAppsWithSessionReplayRoleEnabled (): boolean {
            return this.appIdsWithSessionReplayRoleEnabled.length > 0;
        },
        appIdsWithAdminOrAgentAdminAccess (): string[] {
            const appMap = getAppsStore().map;
            const appIds = Object.keys(appMap);

            const { hasAdminAccess, hasSuperAccess, hasV2SubAdminAccess, hasOpsAccess } = this;

            if (hasAdminAccess || hasSuperAccess || hasV2SubAdminAccess || hasOpsAccess) {
                return appIds;
            }

            return appIds.filter((appId) =>
                $can(appId)
                    .entity('aiAgentPrompt')
                    .action('view'),
            );
        },
        hasAppsWithAdminOrAiAgentAdminAccess (): boolean {
            return this.appIdsWithAdminOrAgentAdminAccess.length > 0;
        },
        accessibleAppIds (): number[] {
            const appIds = Object.keys(getAppsStore().map).map(Number);
            const { hasAdminAccess, hasSuperAccess, hasV2SubAdminAccess } = this;

            if (hasAdminAccess || hasSuperAccess || hasV2SubAdminAccess) return appIds;

            const userAppIds = Object.keys(this.user.applicationAccessControl || {}).map(Number);

            return appIds.filter((id) => userAppIds.includes(id));
        },
        hasAdminOrAiAgentAdminAccess (): (appId: string | number) => boolean {
            return (appId) => {
                return this.appIdsWithAdminOrAgentAdminAccess.some(
                    (existingAppId) => String(existingAppId) === String(appId),
                );
            };
        },
        hasSegmentFlag: (state) => (flag: string): boolean => {
            if (useSegmentFlagOverride()) {
                return true;
            }

            if (!state.segmentFlags?.length) {
                return hasLocalStorageSegmentFlag(flag);
            }

            return state.segmentFlags.includes(flag);
        },
        userSegmentFlags: (state) => state.segmentFlags,
        hasSessionReplayViewerAccess (): () => boolean {
            return () => {
                const { hasSessionReplayEnabled, isPendoFree, isTrainingSubscription } = getSubscriptionStore();
                const { hasAppsWithSessionReplayRoleEnabled } = this;

                const canAccessSessionReplay =
                    hasSessionReplayEnabled &&
                    !isTrainingSubscription &&
                    !isPendoFree &&
                    hasAppsWithSessionReplayRoleEnabled;

                return canAccessSessionReplay;
            };
        },
        hasSessionReplayAdminAccess (): () => boolean {
            return () => {
                const { hasAdminAccess, hasSessionReplayViewerAccess } = this;
                const canAccessSessionReplay = hasSessionReplayViewerAccess() && hasAdminAccess;

                return canAccessSessionReplay;
            };
        },
    },
});

if (module.hot) {
    module.hot.accept(acceptHMRUpdate(useAuthStore, module.hot));
}
